import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  createArticulo,
  updateArticulo,
  type ArticuloItem,
  type ArticuloPayload,
} from '@/shared/api/controllers/articulos';
import { listActiveCategorias } from '@/shared/api/controllers/categorias';
import { listActiveCuentasContables } from '@/shared/api/controllers/cuentasContables';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';

/**
 * Servicios CRUD dialog — mirrors PowerApps `PopUp_AgregarServiciosProductos`
 * (Screen_Configuracion.pa.yaml:6766+). Services only collect three
 * fields:
 *
 *   1. **Categoría** (`Categoria_ART`) — select from `ABM_Categorias`.
 *   2. **Cuenta Contable** (`CuentaContable_ART`) — select from
 *      `ABM_CuentaContable`, cascaded so it's filtered by the picked
 *      Categoría (`Categoria_CC`).
 *   3. **Descripción** (`Articulo_ART`) — free-form service name shown
 *      in the list. We write to `Articulo_ART` on BOTH create and edit;
 *      PowerApps had a bug where edit wrote to `Descripcion_ART` instead
 *      (which the list never rendered). We fix that on our side.
 *
 * Everything else PowerApps wrote with hardcoded defaults:
 *   - `Concat_ART`, `Modelo_ART`, `Codigo_ART`,
 *     `ValorUn_ART`, `Descripcion_ART`: `"-"` placeholder.
 *   - `IVA_ART`: `"-"` — services are invoice-level tax only.
 *   - `Comprable_ART`: `"SI"` — always purchasable.
 *   - `Cotizable_ART`: `"NO"` — flag that distinguishes service from product.
 */

const schema = z.object({
  categoria: z.string().trim().min(1, 'Seleccioná la categoría'),
  cuentaContable: z.string().trim().min(1, 'Seleccioná la cuenta contable'),
  descripcion: z.string().trim().min(1, 'Ingresá la descripción').max(200),
});
type Values = z.infer<typeof schema>;

function itemToForm(item: ArticuloItem): Values {
  return {
    categoria: item.fields.Categoria_ART ?? '',
    cuentaContable: item.fields.CuentaContable_ART ?? '',
    descripcion: item.fields.Articulo_ART ?? '',
  };
}

function emptyValues(): Values {
  return { categoria: '', cuentaContable: '', descripcion: '' };
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: ArticuloItem | null;
  queryKey: readonly string[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export function ServicioDialog({
  open,
  onOpenChange,
  item,
  queryKey,
  onSuccess,
  onError,
}: Props) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const isEdit = Boolean(item);

  const defaults = useMemo(() => (item ? itemToForm(item) : emptyValues()), [item]);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) form.reset(defaults);
  }, [open, defaults, form]);

  const { data: categorias = [] } = useQuery({
    queryKey: ['configuracion', 'categorias', 'active', activeCountry.code],
    queryFn: listActiveCategorias,
    staleTime: 60_000,
  });
  const { data: cuentasContables = [] } = useQuery({
    queryKey: ['configuracion', 'cuentas-contables', 'active', activeCountry.code],
    queryFn: listActiveCuentasContables,
    staleTime: 60_000,
  });

  const categoria = form.watch('categoria');
  const cuentaContable = form.watch('cuentaContable');
  const watchedDescripcion = form.watch('descripcion');

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!categoria) missing.push('Categoría');
    if (!cuentaContable) missing.push('Cuenta Contable');
    if (!watchedDescripcion?.trim()) missing.push('Descripción');
    return missing;
  }, [categoria, cuentaContable, watchedDescripcion]);

  const canSubmit = missingFields.length === 0;

  // Cuenta Contable options filtered to the selected Categoría. Falls
  // back to the full set if the selected cuenta has no category link —
  // keeps legacy rows visible while still encouraging the cascade.
  const filteredCuentas = useMemo(() => {
    if (!categoria) return [];
    return cuentasContables.filter(
      (c) => (c.fields.Categoria_CC ?? '').trim() === categoria.trim(),
    );
  }, [cuentasContables, categoria]);

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      // PowerApps defaults for every field the Servicios UI doesn't
      // surface — kept faithful so downstream readers (facturación,
      // reportes) don't have to special-case services.
      const payload: ArticuloPayload = {
        articulo: values.descripcion,
        codigo: '-',
        modelo: '-',
        descripcion: '-',
        cuentaContable: values.cuentaContable,
        categoria: values.categoria,
        valorUn: null,
        valorUnDolar: null,
        iva: '-',
        cotizable: 'NO',
        comprable: true,
        llevaNroSerie: false,
        impactaStock: false,
      };
      if (isEdit && item) return updateArticulo(item.id, payload);
      return createArticulo(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Cross-feature: Compras / Cotización pickers derive from
      // articles. See `abmCacheInvalidation.ts` for the full map.
      invalidateAbmRelatedQueries(queryClient, 'articulos');
      onSuccess(
        isEdit
          ? 'El servicio se actualizó correctamente.'
          : 'El servicio se creó correctamente.',
      );
    },
    onError: (err) =>
      onError(
        err instanceof Error ? err.message : 'No se pudo guardar el servicio.',
      ),
  });

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values));

  const fieldError = (name: keyof Values) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        processing={mutation.isPending && 'Guardando…'}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Servicio' : 'Agregar Servicio / Producto'}
          </DialogTitle>
          <DialogDescription>
            Elegí categoría y cuenta contable, luego escribí la descripción del
            servicio.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Categoría" required error={fieldError('categoria')}>
                <Select
                  value={categoria}
                  onValueChange={(v) => {
                    form.setValue('categoria', v, { shouldDirty: true });
                    // Reset downstream cuenta on user-driven change.
                    form.setValue('cuentaContable', '', { shouldDirty: true });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map((c) => {
                      const v = c.fields.Categoria_CAT ?? '';
                      return (
                        <SelectItem key={c.id} value={v}>
                          {v || '—'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label="Cuenta Contable"
                required
                error={fieldError('cuentaContable')}
              >
                <Select
                  value={cuentaContable}
                  onValueChange={(v) =>
                    form.setValue('cuentaContable', v, { shouldDirty: true })
                  }
                  disabled={!categoria || filteredCuentas.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !categoria
                          ? 'Elegí categoría'
                          : filteredCuentas.length === 0
                            ? 'Sin cuentas disponibles'
                            : 'Seleccionar...'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCuentas.map((c) => {
                      const v = c.fields.CuentaContable_CC ?? '';
                      return (
                        <SelectItem key={c.id} value={v}>
                          {v || '—'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Descripción" required error={fieldError('descripcion')}>
              <Input
                variant="filled"
                placeholder="Ingresar Descripción"
                {...form.register('descripcion')}
                autoFocus
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  error,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={['space-y-1.5', className].filter(Boolean).join(' ')}>
      <Label>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
